<script setup lang="ts">
import dayjs from 'dayjs'
import type { Icons } from '@/components/base/BaseIcon.vue'
import type { FeedActivity } from '@/types/customer/ActivitiesFeed'
import ActivityCardBase from './ActivityCardBase.vue'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

const { t, locale } = useI18n()
const router = useRouter()
const { isSupportConnection } = useLocalStorage()

const isExpanded = ref(false)

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const messageStatus = computed(() => {
  // SMS status update feature was deployed to production on January 10, 2026.
  // Before this date, we did not have status information for SMS messages.
  // Messages sent before this date are displayed with "delivered" status as a default assumption.
  const statusUpdateDate = dayjs('2026-01-10')
  const messageDate = props.activity.data?.sent_at || props.activity.occurred_at

  // If message was sent before the status update feature deployment, default to "delivered"
  if (messageDate && dayjs(messageDate).isBefore(statusUpdateDate)) {
    return 'delivered'
  }

  const providerStatus = props.activity.data?.status
  const scheduledAt = props.activity.data?.scheduled_at

  // If the message was scheduled and is more than 1 hour old, but we don't have a final status
  // from the provider (no status, or still 'sent'/'pending'), assume it was sent
  // This handles cases where the provider didn't return a delivery status
  if (scheduledAt && (!providerStatus || providerStatus === 'sent' || providerStatus === 'pending')) {
    const scheduledDate = dayjs(scheduledAt)
    const oneHourAgo = dayjs().subtract(1, 'hour')
    if (scheduledDate.isBefore(oneHourAgo)) {
      return 'sent'
    }
  }

  return providerStatus || (props.activity.data?.sent_at ? 'sent' : 'pending')
})

const isFailed = computed(() => {
  return messageStatus.value === 'failed'
})

const isRejected = computed(() => {
  return messageStatus.value === 'rejected'
})

const getMessageTypeKey = (type: string): string => {
  const typeMap: Record<string, string> = {
    'campaign.message.sent': 'campaign',
    'autopilot.message.sent': 'campaign',
    'marketing.flow.message.sent': 'marketingflowmessage',
    'marketing.flow.push.sent': 'marketingflowpush',
    'order.notification.sent': 'ordernotification',
    'reward.message.sent': 'rewardmessage',
    'contest.game.notification.sent': 'contestgame',
    'missed.call.notification.sent': 'missedcall',
    'customer.notification.sent': 'customer'
  }
  return typeMap[type] || 'campaign'
}

const hasTranslation = (key: string): boolean => {
  try {
    const translation = t(key, {}, { default: key })
    return translation !== key
  } catch {
    return false
  }
}

const description = computed(() => {
  const channel = props.activity.data?.channel || 'sms'
  const status = messageStatus.value

  if (status === 'sent' && isProviderStatusMissing.value) {
    return t(`pages.customer.customerDetailPage.blocks.activity.common.text.sent.${channel}`)
  }

  if (status === 'pending' || status === 'sent') {
    return t(`pages.customer.customerDetailPage.blocks.activity.common.text.pending.${channel}`)
  }
  if (status === 'rejected' || status === 'spam_complaint') {
    return t('pages.customer.customerDetailPage.blocks.activity.common.text.rejected')
  }
  if (status === 'failed') {
    return t(`pages.customer.customerDetailPage.blocks.activity.common.text.failed.${channel}`)
  }

  return t(`pages.customer.customerDetailPage.blocks.activity.common.text.${channel}`)
})

const isProviderStatusMissing = computed(() => {
  const providerStatus = props.activity.data?.status
  const scheduledAt = props.activity.data?.scheduled_at
  const displayedStatus = messageStatus.value

  if (
    displayedStatus === 'sent' &&
    scheduledAt &&
    (!providerStatus || providerStatus === 'sent' || providerStatus === 'pending')
  ) {
    const scheduledDate = dayjs(scheduledAt)
    const oneHourAgo = dayjs().subtract(1, 'hour')
    return scheduledDate.isBefore(oneHourAgo)
  }

  return false
})

const iconColor = computed(() => {
  const status = messageStatus.value
  switch (status) {
    case 'pending':
    case 'sent':
      return isProviderStatusMissing.value ? 'bg-green-400' : 'bg-yellow-400'
    case 'delivered':
      return 'bg-green-400'
    case 'failed':
    case 'spam_complaint':
    case 'rejected':
      return 'bg-red-400'
    default:
      return 'bg-foreground-disabled'
  }
})

const formatDate = (dateString: string) => {
  return dayjs(dateString).locale(locale.value).format('DD/MM/YYYY')
}

const formatTime = (dateString: string) => {
  return dayjs(dateString).locale(locale.value).format('HH:mm')
}

const getSourceLabel = (): string => {
  if (props.activity.type === 'autopilot.message.sent') {
    const autopilotSourceKey = 'pages.customer.customerDetailPage.blocks.activity.autopilot.detail.source.default'
    return hasTranslation(autopilotSourceKey)
      ? t(autopilotSourceKey)
      : t('pages.customer.customerDetailPage.blocks.activity.common.detail.source.default')
  }

  const messageTypeKey = getMessageTypeKey(props.activity.type)

  const defaultSourceKey = `pages.customer.customerDetailPage.blocks.activity.${messageTypeKey}.detail.source.default`
  if (hasTranslation(defaultSourceKey)) {
    return t(defaultSourceKey)
  }

  const channel = props.activity.data?.channel || 'sms'
  const commonSourceKey = `pages.customer.customerDetailPage.blocks.activity.common.detail.source.${channel}`
  if (hasTranslation(commonSourceKey)) {
    return t(commonSourceKey)
  }

  return t('pages.customer.customerDetailPage.blocks.activity.common.detail.source.default')
}

const channelLabel = computed(() => getSourceLabel())

const sourceId = computed(() => {
  if (props.activity.data?.campaign_id) {
    return props.activity.data.campaign_id
  }
  const publicId = props.activity.data?.campaign_public_id
  if (!publicId) return null
  const numericId = Number(publicId)
  return isNaN(numericId) ? null : numericId
})

const sourceName = computed(() => {
  if (props.activity.type === 'autopilot.message.sent') {
    return (
      props.activity.data?.autopilot_name?.trim() ||
      t('pages.customer.customerDetailPage.blocks.activity.autopilot.detail.source.default')
    )
  }

  if (props.activity.type === 'campaign.message.sent') {
    return props.activity.data?.campaign_name
  }

  return ''
})

const canOpenSource = computed(() => {
  if (props.activity.type === 'autopilot.message.sent') {
    return false
  }
  return sourceId.value !== null
})

const openSourcePage = () => {
  if (!sourceId.value) return

  const resolvedRoute = router.resolve({
    name: 'CampaignDetail',
    params: { campaignId: sourceId.value.toString() }
  })
  window.open(resolvedRoute.href, '_blank')
}

const statusLabel = computed(() => {
  const status = messageStatus.value

  const failedErrorCodeKeyMap: Record<string, string> = {
    'unknown-error': 'pages.customer.customerDetailPage.blocks.activity.common.detail.status.failed.unknown',
    unroutable: 'pages.customer.customerDetailPage.blocks.activity.common.detail.status.failed.unroutable',
    expired: 'pages.customer.customerDetailPage.blocks.activity.common.detail.status.failed.expired',
    'destination-unreachable':
      'pages.customer.customerDetailPage.blocks.activity.common.detail.status.failed.destination_unreachable',
    'general-error': 'pages.customer.customerDetailPage.blocks.activity.common.detail.status.failed.general_error',
    'absent-subscriber-temporary':
      'pages.customer.customerDetailPage.blocks.activity.common.detail.status.failed.absent_subscriber_temporary'
  }

  if (status === 'failed' && props.activity.data?.external_error_code) {
    const errorCode = props.activity.data.external_error_code.toString().trim().toLowerCase()
    const errorKey = failedErrorCodeKeyMap[errorCode]
    if (errorKey) {
      return t(errorKey)
    }
    return t('pages.customer.customerDetailPage.blocks.activity.common.detail.status.failed.default')
  }

  if (status === 'spam_complaint') {
    return t('pages.customer.customerDetailPage.blocks.activity.common.detail.status.rejected.spam')
  }

  if (status === 'failed') {
    return t('pages.customer.customerDetailPage.blocks.activity.common.detail.status.failed.default')
  }
  if (status === 'rejected') {
    return t('pages.customer.customerDetailPage.blocks.activity.common.detail.status.rejected.default')
  }
  return t(`pages.customer.customerDetailPage.blocks.activity.common.detail.status.${status}`)
})

const seeDetailsLabel = computed(() => {
  return t('pages.customer.customerDetailPage.blocks.activity.common.detail.seeDetails')
})

const sourceLabel = computed(() => {
  return t('pages.customer.customerDetailPage.blocks.activity.common.detail.sourceLabel')
})

const statusLabelText = computed(() => {
  return t('pages.customer.customerDetailPage.blocks.activity.common.detail.statusLabel')
})

const messageLabel = computed(() => {
  return t('pages.customer.customerDetailPage.blocks.activity.common.detail.messageLabel')
})

const isConverted = computed(() => {
  return Boolean(props.activity.data?.converted_at)
})

const conversionDateText = computed(() => {
  if (!isConverted.value || !props.activity.data?.converted_at) return ''

  const convertedDate = dayjs(props.activity.data.converted_at).locale(locale.value)
  const messageTypeKey = getMessageTypeKey(props.activity.type)
  return t(`pages.customer.customerDetailPage.blocks.activity.${messageTypeKey}.detail.conversionDate`, {
    date: convertedDate.format('DD/MM/YYYY'),
    time: convertedDate.format('HH:mm')
  })
})

const statusClasses = computed(() => {
  const status = messageStatus.value
  // If the provider status is missing but message was sent, display 'sent' in green
  if (status === 'sent' && isProviderStatusMissing.value) {
    return 'bg-green-50 text-green-800'
  }
  switch (status) {
    case 'pending':
    case 'sent':
      return 'bg-yellow-50 text-yellow-800'
    case 'delivered':
      return 'bg-green-50 text-green-800'
    case 'failed':
    case 'spam_complaint':
    case 'rejected':
      return 'bg-red-50 text-red-800'
    default:
      return 'bg-muted text-foreground'
  }
})

const errorBlockClasses = computed(() => {
  const status = messageStatus.value
  switch (status) {
    case 'failed':
    case 'spam_complaint':
    case 'rejected':
      return {
        bg: 'bg-red-50',
        border: 'border-red-800',
        text: 'text-red-800'
      }
    default:
      return {
        bg: 'bg-muted',
        border: 'border-border',
        text: 'text-foreground'
      }
  }
})

const mapErrorCodeToTranslationKey = (rawCode: string | null | undefined): string | null => {
  if (!rawCode) return null

  const trimmed = rawCode.toString().trim()
  const normalized = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '_')

  if (normalized === 'delivered' || normalized === 'success') {
    return null
  }

  const baseKey = 'pages.customer.customerDetailPage.blocks.activity.common.detail.errors.'

  // Map according to the UX specification table
  if (normalized === 'unknown' || normalized === 'unknown_error' || normalized === 'unknown-error') {
    return baseKey + 'unknown'
  }

  if (normalized === 'unroutable') {
    return baseKey + 'unroutable'
  }

  if (normalized === 'spam_complaint' || normalized === 'spam-complaint') {
    return baseKey + 'spam_complaint'
  }

  if (normalized === 'expired') {
    return baseKey + 'expired'
  }

  if (normalized === 'destination_unreachable' || normalized === 'destination-unreachable') {
    return baseKey + 'destination_unreachable'
  }

  if (normalized === 'general_error' || normalized === 'general-error') {
    return baseKey + 'general_error'
  }

  if (
    normalized.includes('absent_subscriber_temporary') ||
    normalized.includes('absent-subscriber-temporary') ||
    normalized.includes('temporary') ||
    normalized.includes('busy') ||
    normalized.includes('network_error')
  ) {
    return baseKey + 'absent_subscriber_temporary'
  }

  if (
    normalized.includes('absent_subscriber_permanent') ||
    normalized.includes('permanent') ||
    normalized.includes('number_inactive')
  ) {
    return baseKey + 'number_inactive'
  }

  if (
    normalized.includes('call_barred') ||
    normalized.includes('illegal_number') ||
    normalized.includes('number_blocked') ||
    normalized.includes('optout') ||
    normalized.includes('opt_out')
  ) {
    return baseKey + 'blocked'
  }

  if (normalized.includes('portability')) {
    return baseKey + 'portability'
  }

  if (
    normalized.includes('anti_spam') ||
    (normalized.includes('spam') && !normalized.includes('spam_complaint')) ||
    normalized.includes('content_filter') ||
    normalized.includes('entity_filter') ||
    normalized.includes('header_filter')
  ) {
    return baseKey + 'spam_or_content'
  }

  if (normalized.includes('age_restriction')) {
    return baseKey + 'age_restriction'
  }

  if (normalized.includes('insufficient_funds')) {
    return baseKey + 'insufficient_credit'
  }

  if (
    normalized.includes('quota_exceeded') ||
    normalized.includes('daily_limit') ||
    normalized.includes('limit_surpassed')
  ) {
    return baseKey + 'limit_exceeded'
  }

  if (
    normalized.includes('fraud_defender') ||
    normalized.includes('burst_protection') ||
    normalized.includes('sequential_dialing')
  ) {
    return baseKey + 'fraud_protection'
  }

  if (normalized.includes('illegal_sender_address') || normalized.includes('us_destination')) {
    return baseKey + 'us_sender'
  }

  if (
    normalized.includes('regulation') ||
    normalized.includes('regulation_error') ||
    normalized.includes('consent_filter')
  ) {
    return baseKey + 'regulation_or_consent'
  }

  return baseKey + 'unknown'
}

const errorMessage = computed(() => {
  const key = mapErrorCodeToTranslationKey(props.activity.data?.external_error_code ?? null)
  return key ? t(key) : null
})

const missingProviderStatusMessage = computed(() => {
  if (isProviderStatusMissing.value) {
    return t('pages.customer.customerDetailPage.blocks.activity.common.detail.errors.status_not_provided')
  }
  return null
})

const messageBody = computed(() => {
  return props.activity.data?.message_content || props.activity.data?.message || ''
})

const isEmailChannel = computed(() => {
  const channel = props.activity.data?.channel
  return channel === 'mail'
})

const extractBodyContent = (html: string): string => {
  if (!html) return ''

  const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i)
  if (bodyMatch && bodyMatch[1]) {
    return bodyMatch[1].trim()
  }

  const htmlMatch = html.match(/<html[^>]*>([\s\S]*)<\/html>/i)
  if (htmlMatch && htmlMatch[1]) {
    return htmlMatch[1].replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '').trim()
  }

  return html
}

const isHtmlContent = computed(() => {
  if (!isEmailChannel.value) return false
  const content = messageBody.value
  if (!content) return false
  return /<[a-z][\s\S]*>/i.test(content)
})

const cleanedHtmlContent = computed(() => {
  if (!isHtmlContent.value) return messageBody.value
  const extractedContent = extractBodyContent(messageBody.value)
  return sanitizeHtml(extractedContent)
})

const CHANNEL_ICONS: Record<string, Icons> = {
  mail: 'HeroEnvelopeIconSolid'
}

const DEFAULT_ICON: Icons = 'HeroChatBubbleOvalLeftEllipsisIconSolid'

const activityIcon = computed<Icons>(() => {
  return (props.activity.icon as Icons) ?? CHANNEL_ICONS[props.activity.data?.channel] ?? DEFAULT_ICON
})
</script>

<template>
  <ActivityCardBase
    :icon="activityIcon"
    :icon-color="iconColor"
    :occurred-at="activity.occurred_at"
    :show-connector="showConnector"
  >
    <template #description>
      <div class="space-y-0.4">
        <p class="text-sm text-muted-foreground">
          {{ description }}
        </p>

        <button
          v-if="messageBody"
          type="button"
          class="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground-secondary focus:outline-hidden cursor-pointer duration-300"
          @click="isExpanded = !isExpanded"
        >
          <span>{{ seeDetailsLabel }}</span>
          <BaseIcon
            name="HeroChevronDownIconSolid"
            class="h-4 w-4 transition-transform duration-200"
            :class="{ 'rotate-180': isExpanded }"
          />
        </button>
      </div>
    </template>

    <template #footer>
      <div
        v-show="isExpanded && messageBody"
        class="mt-4 self-stretch rounded-lg border border-border bg-white p-3 outline-1 outline-offset-[-1px] outline-border ml-8"
      >
        <div class="self-stretch flex flex-col justify-start items-start gap-3">
          <div class="self-stretch flex flex-col justify-start items-start gap-1.5">
            <div class="self-stretch flex flex-col justify-center items-start">
              <div class="self-stretch justify-center text-sm font-medium text-muted-foreground">
                {{ sourceLabel }} : {{ channelLabel }}
              </div>
            </div>
            <div v-if="sourceName" class="self-stretch flex gap-1.5 items-start">
              <div class="flex flex-col justify-center text-sm font-medium text-foreground">
                {{ sourceName }}
              </div>
              <button
                v-if="canOpenSource"
                type="button"
                class="w-4 h-4 relative overflow-hidden cursor-pointer group focus:outline-none focus:ring-2 focus:ring-legacyPrimary focus:ring-offset-1 rounded transition-all shrink-0"
                :title="t('pages.customer.customerDetailPage.blocks.activity.campaign.detail.openCampaign')"
                @click.stop="openSourcePage"
              >
                <BaseIcon
                  name="HeroArrowTopRightOnSquareIconSolid"
                  class="h-4 w-4 text-muted-foreground group-hover:text-legacyPrimary group-hover:scale-110 transition-all"
                />
              </button>
            </div>
            <div v-if="isConverted && conversionDateText" class="self-stretch flex flex-col justify-center items-start">
              <div class="text-sm text-muted-foreground">
                {{ conversionDateText }}
              </div>
            </div>
          </div>

          <div class="self-stretch flex flex-col justify-start items-start gap-1.5">
            <template v-if="isSupportConnection">
              <div class="self-stretch flex flex-col justify-center items-start">
                <div class="self-stretch justify-center text-sm font-medium text-muted-foreground">
                  {{ statusLabelText }}
                </div>
              </div>
              <div
                class="px-2 py-0.5 rounded-md inline-flex justify-center items-center gap-1.5"
                :class="statusClasses"
              >
                <div class="justify-start text-xs font-normal leading-5">
                  {{ statusLabel }}
                </div>
              </div>
            </template>

            <div
              v-if="errorMessage && (isFailed || isRejected)"
              class="self-stretch p-3 rounded-lg border flex flex-col justify-start items-start"
              :class="[errorBlockClasses.bg, errorBlockClasses.border]"
            >
              <div class="self-stretch flex flex-col justify-start items-start">
                <div class="self-stretch flex items-start">
                  <div
                    class="flex-1 flex flex-col justify-center text-xs font-normal leading-5"
                    :class="errorBlockClasses.text"
                  >
                    {{ errorMessage }}
                  </div>
                </div>
              </div>
            </div>
            <BaseTip
              v-if="missingProviderStatusMessage"
              severity="info"
              :text="missingProviderStatusMessage"
              class="self-stretch"
            />
          </div>

          <div v-if="messageBody" class="self-stretch flex flex-col justify-start items-start gap-2.5">
            <div class="self-stretch flex flex-col justify-center items-start">
              <div class="self-stretch justify-center text-sm font-medium text-muted-foreground">
                {{ messageLabel }}
              </div>
            </div>
            <div
              class="self-stretch p-3 bg-muted rounded-lg outline-1 outline-offset-[-1px] outline-border flex flex-col justify-start items-start gap-2.5"
            >
              <div class="self-stretch flex flex-col justify-start items-start gap-2.5">
                <div class="self-stretch inline-flex justify-start items-start gap-1">
                  <div
                    v-if="isHtmlContent"
                    class="flex-1 self-stretch text-foreground text-sm font-medium email-content"
                    v-html="cleanedHtmlContent"
                  />
                  <div
                    v-else
                    class="flex-1 self-stretch justify-center text-foreground text-sm font-medium whitespace-pre-wrap break-words"
                  >
                    {{ messageBody }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #date>
      <div>{{ formatDate(activity.occurred_at) }}</div>
      <div>{{ formatTime(activity.occurred_at) }}</div>
    </template>
  </ActivityCardBase>
</template>

<style scoped>
.email-content {
  max-width: 100%;
  overflow-x: auto;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.email-content :deep(body),
.email-content :deep(html) {
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 100%;
}

.email-content :deep(table) {
  max-width: 100%;
  width: 100% !important;
  table-layout: auto;
}

.email-content :deep(img) {
  max-width: 100%;
  height: auto;
  display: block;
}

.email-content :deep(div),
.email-content :deep(p),
.email-content :deep(span),
.email-content :deep(td),
.email-content :deep(th) {
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.email-content :deep(*) {
  box-sizing: border-box;
}
</style>
